import { useCallback, useRef, useEffect } from 'react';
import { useWidgetStore } from '../store';
import SessionService from '../services/SessionService';

interface UseSessionLifecycleReturn {
  sessionId: string | null;
  createSession: (id: string) => boolean;
  endSession: () => Promise<void>;
  isSessionActive: boolean;
}

export const useSessionLifecycle = (
  apiUrl: string,
  debug: boolean = false
): UseSessionLifecycleReturn => {
  const sessionId = useWidgetStore((state) => state.backendSessionId);

  const sessionService = useRef(
    new SessionService({ apiUrl }, debug)
  );

  const createSession = useCallback((id: string) => {
    const created = sessionService.current.createSession(id);
    if (created) useWidgetStore.getState().setBackendSessionId(id);
    return created;
  }, []);

  const endSession = useCallback(async () => {
    const ownedSessionId = sessionService.current.getCurrentSessionId();
    await sessionService.current.endSession();
    if (ownedSessionId && useWidgetStore.getState().backendSessionId === ownedSessionId) {
      useWidgetStore.getState().setBackendSessionId(null);
    }
  }, []);

  const isSessionActive = !!sessionId;

  // Cleanup on unmount
  useEffect(() => {
    const service = sessionService.current;
    service.mount();
    return () => {
      const ownedSessionId = service.getCurrentSessionId();
      service.destroy();
      if (ownedSessionId && useWidgetStore.getState().backendSessionId === ownedSessionId) {
        useWidgetStore.getState().setBackendSessionId(null);
      }
    };
  }, []);

  return {
    sessionId,
    createSession,
    endSession,
    isSessionActive,
  };
};
